/**
 * @file `<ClosingFeedback/>` — the closing chapter's feedback affordance.
 * @module @labcraft/lab/ui/components/closing_feedback
 *
 * Umbrella #1446 Part E. The Part flow's `closing` chapter is
 * objective-free — its sole learner interaction is feedback on the Part.
 * This component captures an optional 1–5 rating plus the per-Part prompts
 * (what was clear, what was useful, where the learner struggled) and a
 * general-experience note, then posts them via the injected `onSubmit`
 * (which calls `lab.chapter.submitPartFeedback` server-side, where the
 * mentor/recalibration signal is emitted). At least one field must be
 * filled for Submit to enable; a failed post surfaces + offers retry, and
 * a success swaps the form for a thank-you.
 */

import { useState, type JSX } from 'react'
import type { PartFeedbackRequest } from '../../shared/routes.ts'
import styles from './closing_feedback.module.css'

/**
 * Props for {@link ClosingFeedback}.
 *
 * @property onSubmit - persists the feedback (throws on 4xx so the form can
 *   surface the failure); the platform binds it to the current Part.
 */
export interface ClosingFeedbackProps {
  onSubmit: (feedback: PartFeedbackRequest) => Promise<void>
}

/** One free-text prompt's local state + label. */
type Prompt = {
  key: 'clear' | 'useful' | 'struggled' | 'experience'
  label: string
  placeholder: string
}

const PROMPTS: readonly Prompt[] = [
  {
    key: 'clear',
    label: 'What clicked?',
    placeholder: 'The idea or moment that made sense…',
  },
  {
    key: 'useful',
    label: 'What was useful?',
    placeholder: 'Something you expect to reuse…',
  },
  {
    key: 'struggled',
    label: 'Where did you struggle?',
    placeholder: 'A step that felt shaky or unclear…',
  },
  {
    key: 'experience',
    label: 'Anything else?',
    placeholder: 'How the Part felt overall…',
  },
]

const RATINGS = [1, 2, 3, 4, 5] as const

export function ClosingFeedback(props: ClosingFeedbackProps): JSX.Element {
  const [rating, setRating] = useState<number | undefined>(undefined)
  const [text, setText] = useState<Record<Prompt['key'], string>>({
    clear: '',
    useful: '',
    struggled: '',
    experience: '',
  })
  const [status, setStatus] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle')

  const build = (): PartFeedbackRequest => {
    const feedback: PartFeedbackRequest = {}
    if (rating !== undefined) feedback.rating = rating
    for (const { key } of PROMPTS) {
      const value = text[key].trim()
      if (value) feedback[key] = value
    }
    return feedback
  }

  const hasAny =
    rating !== undefined || PROMPTS.some(p => text[p.key].trim().length > 0)

  const submit = async (): Promise<void> => {
    if (!hasAny || status === 'pending') return
    setStatus('pending')
    try {
      await props.onSubmit(build())
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <section className={styles.card} data-testid="feedback-thanks">
        <h2 className={styles.title}>Thanks — that helps.</h2>
        <p className={styles.body}>
          Your notes feed straight into how this Part gets sharpened next.
        </p>
      </section>
    )
  }

  return (
    <section className={styles.card} data-testid="closing-feedback">
      <h2 className={styles.title}>How was this Part?</h2>
      <p className={styles.body}>
        A minute of feedback shapes what this Part becomes. Rate it, drop a
        note, or both.
      </p>

      <div
        className={styles.rating}
        role="radiogroup"
        aria-label="Rate this Part">
        {RATINGS.map(value => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={rating === value}
            aria-label={`${value} out of 5`}
            data-testid={`rating-${value}`}
            className={`${styles.star}${
              rating !== undefined && value <= rating ? ` ${styles.starOn}` : ''
            }`}
            onClick={() => setRating(value)}>
            ★
          </button>
        ))}
      </div>

      {PROMPTS.map(prompt => (
        <label key={prompt.key} className={styles.field}>
          <span className={styles.fieldLabel}>{prompt.label}</span>
          <textarea
            className={styles.textarea}
            rows={2}
            maxLength={2000}
            placeholder={prompt.placeholder}
            data-testid={`feedback-${prompt.key}`}
            value={text[prompt.key]}
            onChange={e =>
              setText(prev => ({ ...prev, [prompt.key]: e.target.value }))
            }
          />
        </label>
      ))}

      {status === 'error' && (
        <p className={styles.error} role="alert" data-testid="feedback-error">
          Couldn’t send your feedback. Check your connection and try again.
        </p>
      )}

      <button
        type="button"
        className={styles.submit}
        data-testid="feedback-submit"
        disabled={!hasAny || status === 'pending'}
        onClick={() => void submit()}>
        {status === 'pending' ? 'Sending…' : 'Send feedback'}
      </button>
    </section>
  )
}
